import { parseSeasonStatValue } from "../utils/playerSeasonChart";
import { scoreboardNameFromPlayer } from "../utils/playerDisplayName";
import { buildSeasonChartSeries } from "../utils/seasonChartSeries";
import { compareYearKeys, sortYearKeys } from "../utils/yearKey";
import {
  NAME_SORT_ORDER,
  STAT_SORT_ORDER,
  sortedClassFor,
} from "../utils/resultsGrid";

export const NBA_STAT_GROUPS = [
  { id: "totals", label: "Totals" },
  { id: "perGame", label: "Per game" },
];

const TOTALS_FIELDS = [
  { id: "year", label: "Season", format: "season", width: 110 },
  { id: "team", label: "Tm", format: "text", width: 72 },
  { id: "g", label: "G", format: "count", width: 64, chart: true },
  { id: "mp", label: "MP", format: "count", width: 72 },
  { id: "pts", label: "PTS", format: "count", width: 80, chart: true },
  { id: "trb", label: "REB", format: "count", width: 72, chart: true },
  { id: "ast", label: "AST", format: "count", width: 72, chart: true },
  { id: "stl", label: "STL", format: "count", width: 64, chart: true },
  { id: "blk", label: "BLK", format: "count", width: 64, chart: true },
  { id: "tov", label: "TOV", format: "count", width: 64 },
  { id: "fg_pct", label: "FG%", format: "pct", width: 72, chart: true },
  { id: "fg3_pct", label: "3P%", format: "pct", width: 72 },
  { id: "ft_pct", label: "FT%", format: "pct", width: 72 },
];

const PER_GAME_FIELDS = [
  { id: "year", label: "Season", format: "season", width: 110 },
  { id: "team", label: "Tm", format: "text", width: 72 },
  { id: "g", label: "G", format: "count", width: 64 },
  { id: "mp_per_g", label: "MPG", format: "decimal1", width: 72 },
  { id: "pts_per_g", label: "PPG", format: "decimal1", width: 72, chart: true },
  { id: "trb_per_g", label: "RPG", format: "decimal1", width: 72, chart: true },
  { id: "ast_per_g", label: "APG", format: "decimal1", width: 72, chart: true },
  { id: "stl_per_g", label: "SPG", format: "decimal1", width: 72, chart: true },
  { id: "blk_per_g", label: "BPG", format: "decimal1", width: 72, chart: true },
  { id: "fg_pct", label: "FG%", format: "pct", width: 72 },
  { id: "fg3_pct", label: "3P%", format: "pct", width: 72 },
  { id: "ft_pct", label: "FT%", format: "pct", width: 72 },
];

const FIELDS_BY_GROUP = {
  totals: TOTALS_FIELDS,
  perGame: PER_GAME_FIELDS,
};

const DEFAULT_CHART_STAT = {
  totals: "pts",
  perGame: "pts_per_g",
};

export const NBA_COMPARE_PRESETS = [
  { label: "James vs Jordan", players: ["jamesle01", "jordami01"] },
  { label: "Magic vs Bird", players: ["johnsma02", "birdla01"] },
];

const NBA_COMPARE_LOWER_IS_BETTER = new Set(["tov"]);

const isCompareValueField = (field) =>
  field.id !== "year" && field.id !== "team";

export function getNbaCompareStats(groupId = "totals") {
  return getNbaGridFields(groupId)
    .filter(isCompareValueField)
    .map((field) => ({
      id: field.id,
      label: field.label,
      group: groupId,
      format: field.format,
    }));
}

export const getNbaPlayerPath = (playerId) =>
  playerId ? `/app/nba/player/${playerId}` : "/app/nba/players";

export function getNbaDisplayName(player, fallback = "Unknown Player") {
  return scoreboardNameFromPlayer(player, fallback);
}

export const getNbaComparePath = (playerIds = []) => {
  const ids = playerIds.filter(Boolean);
  if (ids.length === 0) return "/app/nba/compare";
  return `/app/nba/compare?players=${ids.join(",")}`;
};

export function getNbaGridFields(groupId) {
  return FIELDS_BY_GROUP[groupId] || TOTALS_FIELDS;
}

export function getNbaChartStats(groupId) {
  return getNbaGridFields(groupId).filter((field) => field.chart);
}

export function getNbaDefaultChartStat(groupId) {
  return DEFAULT_CHART_STAT[groupId] || DEFAULT_CHART_STAT.totals;
}

export function getNbaFieldGroup(fieldId) {
  return fieldId?.endsWith("_per_g") ? "perGame" : "totals";
}

export function playerHasNbaSeasons(player) {
  return NBA_STAT_GROUPS.some(
    (group) => Object.keys(getNbaSeasonMap(player, group.id)).length > 0
  );
}

export function getNbaSeasonMap(player, groupId) {
  return player?.stats?.[groupId]?.seasons || {};
}

export function getNbaCareer(player, groupId) {
  return player?.stats?.[groupId]?.career || {};
}

export function getNbaStatValue(row, fieldId) {
  if (!row || !fieldId) return null;
  return parseSeasonStatValue(row[fieldId]);
}

export function formatNbaStatValue(value, format = "count") {
  if (format === "season" || format === "text") {
    return value == null || value === "" ? "—" : String(value);
  }
  const num = parseSeasonStatValue(value);
  if (num == null) return "—";
  if (format === "decimal1") return num.toFixed(1);
  if (format === "pct") {
    const pct = num <= 1 ? num * 100 : num;
    return `${pct.toFixed(1)}%`;
  }
  return Math.round(num).toLocaleString();
}

export function buildNbaSeasonRows(player, groupId) {
  const seasons = getNbaSeasonMap(player, groupId);
  const valueFields = getNbaGridFields(groupId).filter(
    (field) => field.id !== "year" && field.id !== "team"
  );

  return sortYearKeys(Object.keys(seasons), "desc").map((yearKey) => {
    const season = seasons[yearKey] || {};
    const row = {
      id: yearKey,
      year: yearKey,
      team: season.team_id || season.team || null,
    };
    valueFields.forEach((field) => {
      row[field.id] = getNbaStatValue(season, field.id);
    });
    return row;
  });
}

export function buildNbaSeasonChartPoints(player, fieldId) {
  const groupId = getNbaFieldGroup(fieldId);
  return buildSeasonChartSeries(getNbaSeasonMap(player, groupId), (row) =>
    getNbaStatValue(row, fieldId)
  );
}

export function getNbaSeasonColumns(groupId, sortedField = "year") {
  return getNbaGridFields(groupId).map((field) => {
    const isYear = field.id === "year";
    const isText = field.format === "text" || isYear;
    return {
      field: field.id,
      headerName: field.label,
      width: field.width,
      type: isText ? undefined : "number",
      align: isText ? "left" : "right",
      headerAlign: isText ? "left" : "right",
      sortingOrder: isYear ? STAT_SORT_ORDER : isText ? NAME_SORT_ORDER : STAT_SORT_ORDER,
      headerClassName: sortedClassFor(field.id, sortedField),
      cellClassName: sortedClassFor(field.id, sortedField),
      ...(isYear ? { sortComparator: compareYearKeys } : {}),
      valueFormatter: ({ value }) => formatNbaStatValue(value, field.format),
    };
  });
}

export function getNbaCompareCareerRowDefs(groupId = "totals") {
  const valueFields = getNbaGridFields(groupId).filter(isCompareValueField);
  return [
    {
      id: "seasons",
      key: "seasons",
      label: "Seasons",
      format: "count",
      computed: "seasonCount",
    },
    ...valueFields.map((field) => ({
      id: field.id,
      key: field.id,
      label: field.label,
      format: field.format,
      group: groupId,
      lowerIsBetter: NBA_COMPARE_LOWER_IS_BETTER.has(field.id),
    })),
  ];
}

export function getNbaCompareCareerValue(player, field, groupId = "totals") {
  if (field?.computed === "seasonCount" || field?.id === "seasons") {
    const count = Object.keys(getNbaSeasonMap(player, groupId)).length;
    return count > 0 ? count : null;
  }
  return getNbaStatValue(getNbaCareer(player, field.group || groupId), field.id);
}

export function getNbaCompareCareerRows(players = [], groupId = "totals") {
  return getNbaCompareCareerRowDefs(groupId).map((field) => ({
    ...field,
    values: players.map((player) =>
      getNbaCompareCareerValue(player, field, groupId)
    ),
  }));
}

export function getNbaCompareStat(statId, groupId) {
  const resolvedGroup = groupId || getNbaFieldGroup(statId);
  const stats = getNbaCompareStats(resolvedGroup);
  return (
    stats.find((stat) => stat.id === statId) ||
    stats.find((stat) => stat.id === getNbaDefaultChartStat(resolvedGroup)) ||
    stats[0]
  );
}

export function buildNbaCompareNamedSeries(players = [], fieldId) {
  return players.map((player) => ({
    name: getNbaDisplayName(player),
    points: buildNbaSeasonChartPoints(player, fieldId),
  }));
}

export function getNbaCompareSubtitle(player, groupId = "totals") {
  const bio = player?.bio || player?.bioData || {};
  const parts = [];
  if (bio.position) parts.push(bio.position);

  const seasons = sortYearKeys(Object.keys(getNbaSeasonMap(player, groupId)));
  if (seasons.length > 0) {
    const first = seasons[0];
    const last = seasons[seasons.length - 1];
    parts.push(first === last ? first : `${first}–${last}`);
  }

  return parts.length > 0 ? parts.join(" · ") : null;
}

export function hasNbaCareerStats(player, groupId = "totals") {
  const career = getNbaCareer(player, groupId);
  if (career && Object.keys(career).length > 0) return true;
  return Object.keys(getNbaSeasonMap(player, groupId)).length > 0;
}
